/**
 * Organization sign-in gate. When Entra auth is configured, the whole app sits
 * behind an Office 365 login restricted to the BGD tenant; when it isn't
 * (local dev, or before the Azure registration exists), children render
 * directly and nothing changes.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  authDevBypass,
  msalConfigured,
  getMsalInstance,
  ensureMsalInitialized,
  checkSessionOnline,
  loginInteractive,
  purgeSession,
  reloadApp,
  redirectForInteraction,
} from './msal';
import { readStamp, withinGrace } from './session';
import { logUsage } from './usage';
import './AuthGate.css';

/** Background re-check cadence while the app stays open. */
const REVALIDATE_INTERVAL_MS = 60 * 60 * 1000;

/**
 * How long "Verifying your access…" may stay up before the screen offers a
 * way out. MSAL's own ceilings are a 10 s hidden-iframe wait and a 30 s
 * redirect navigation; a healthy check answers in well under either.
 */
export const CHECK_WATCHDOG_MS = 20_000;

/**
 * Take the user to Microsoft; if the browser cannot even start the redirect,
 * fall back to a clean sign-in gate rather than a button that does nothing.
 */
async function signInAgain(): Promise<void> {
  try {
    await redirectForInteraction();
  } catch {
    await purgeSession();
    reloadApp();
  }
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function SignInScreen() {
  const { inProgress } = useMsal();
  const [failed, setFailed] = useState(false);
  const busy = inProgress !== InteractionStatus.None;
  const signIn = () => {
    setFailed(false);
    loginInteractive().catch(() => setFailed(true));
  };
  return (
    <div className="auth-gate-screen">
      <div className="auth-gate-card">
        <div className="auth-gate-logo">BGD</div>
        <h1>BGD Electrical Calculator</h1>
        <p>Sign in with your BGD Engineering Microsoft 365 account to continue.</p>
        <button className="auth-gate-signin" disabled={busy} onClick={signIn}>
          <MicrosoftMark />
          {busy ? 'Signing in…' : 'Sign in with Microsoft'}
        </button>
        {failed && (
          <p role="alert">
            The browser could not open the Microsoft sign-in page. Reload and try again.
          </p>
        )}
      </div>
    </div>
  );
}

/** Loop guard: one interactive redirect attempt per short window. */
const REDIRECT_ATTEMPT_KEY = 'bgd-auth-redirect-attempted';
const REDIRECT_LOOP_WINDOW_MS = 2 * 60 * 1000;

function forgetRedirectAttempt(): void {
  try {
    sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
}

type GuardState = 'ok' | 'checking' | 'blocked' | 'stalled';

/**
 * Post-login revalidation: confirms with Microsoft that the cached account is
 * still good — on mount, hourly, and whenever the connection returns.
 *
 *  - 'valid': stamp refreshed, work continues.
 *  - 'interaction' while BLOCKING (grace expired / no stamp): bounce through
 *    the interactive Microsoft sign-in. For an active employee this completes
 *    against their live Entra session in seconds; for a disabled account
 *    Microsoft refuses the sign-in at its own door — revocation enforced by
 *    the authority itself, never by a local lockout screen. A loop guard
 *    purges to the sign-in gate if the redirect bounces straight back.
 *  - 'interaction' while IN grace: keep working — never yank someone to a
 *    login page mid-edit; the next launch (or grace expiry) resolves it.
 *  - 'offline' + within grace: keep working (field laptops).
 *  - 'offline' + grace expired: "Reconnect" screen, with a manual sign-in
 *    escape hatch.
 *
 * Two things keep the verifying screen from becoming a dead end:
 *
 *  - Nothing is checked while MSAL is still processing a redirect response
 *    (`inProgress` is not None). This component mounts during that window —
 *    the returning account is already in the cache — and a forced refresh
 *    then races the code exchange using the OLD refresh token, fails, and
 *    either purges the fresh session (loop guard) or throws
 *    `interaction_in_progress` from a second redirect. When MSAL finishes,
 *    the interactive result has already been stamped, so the user is admitted
 *    without a round trip.
 *  - A watchdog turns a check that neither answers nor navigates into a
 *    screen with Retry and Sign in, and any error thrown along the way lands
 *    on that same screen instead of leaving the spinner up.
 */
export function SessionGuard({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const accountId = account?.homeAccountId;
  const inGrace = useCallback(
    () => accountId !== undefined && withinGrace(readStamp(), accountId),
    [accountId]
  );

  const [state, setState] = useState<GuardState>(() => (inGrace() ? 'ok' : 'checking'));
  const stateRef = useRef<GuardState>(state);
  const setGuard = useCallback((next: GuardState) => {
    stateRef.current = next;
    setState(next);
  }, []);
  const msalIdle = inProgress === InteractionStatus.None;
  const startedRef = useRef(false);

  // A redirect that just completed was stamped as its response was processed.
  // Once MSAL reports idle, that stamp admits the user directly — no round
  // trip against a token cache that was being rewritten a moment ago — and
  // the check below runs in the background like any other in-grace launch.
  const admitted = state === 'ok' || (state === 'checking' && msalIdle && inGrace());

  const validate = useCallback(() => {
    // Anything but an admitted user is a screen they are stuck behind.
    const blocking = stateRef.current !== 'ok' && !inGrace();
    if (!blocking) forgetRedirectAttempt(); // a bounce cannot be in progress
    checkSessionOnline()
      .then(async (result) => {
        if (result === 'valid') {
          forgetRedirectAttempt();
          setGuard('ok');
          return;
        }
        if (result !== 'interaction') {
          // Offline: within grace keep working silently; otherwise this was
          // a blocking check that failed — show the reconnect screen.
          if (blocking) setGuard('blocked');
          return;
        }
        if (!blocking) return; // in grace: keep working, resolve on next launch
        let lastAttempt = 0;
        try {
          lastAttempt = Number(sessionStorage.getItem(REDIRECT_ATTEMPT_KEY) ?? 0);
        } catch {
          /* ignore */
        }
        if (Date.now() - lastAttempt < REDIRECT_LOOP_WINDOW_MS) {
          // The interactive round-trip just failed to produce a session —
          // fall back to a clean sign-in gate instead of looping.
          await purgeSession();
          reloadApp();
          return;
        }
        try {
          sessionStorage.setItem(REDIRECT_ATTEMPT_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        await redirectForInteraction(); // navigates away
      })
      .catch(() => {
        // MSAL threw instead of answering or navigating. Offer the way out.
        if (blocking) setGuard('stalled');
      });
  }, [inGrace, setGuard]);

  useEffect(() => {
    if (!msalIdle) return;
    if (!startedRef.current) {
      startedRef.current = true;
      logUsage('session-start');
    }
    validate();
    const interval = window.setInterval(validate, REVALIDATE_INTERVAL_MS);
    window.addEventListener('online', validate);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', validate);
    };
  }, [msalIdle, validate]);

  // Watchdog: a verification that has neither answered nor navigated.
  useEffect(() => {
    if (admitted || state !== 'checking') return;
    const timer = window.setTimeout(() => setGuard('stalled'), CHECK_WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [admitted, state, setGuard]);

  const retry = () => {
    setGuard('checking');
    // MSAL still busy: the effect above re-checks the moment it goes idle.
    if (msalIdle) validate();
  };

  if (admitted) return <>{children}</>;

  return (
    <div className="auth-gate-screen">
      <div className="auth-gate-card">
        <div className="auth-gate-logo">BGD</div>
        {state === 'checking' && (
          <>
            <h1>Verifying your access…</h1>
            <p>Checking your BGD Engineering account with Microsoft.</p>
          </>
        )}
        {state === 'stalled' && (
          <>
            <h1>Still verifying…</h1>
            <p>
              Microsoft has not answered yet. Retry, or sign in again directly — that takes a
              few seconds against your live account.
            </p>
            <button className="auth-gate-signin" onClick={retry}>
              Retry
            </button>
            <button className="auth-gate-signin" onClick={() => void signInAgain()}>
              <MicrosoftMark />
              Sign in with Microsoft
            </button>
          </>
        )}
        {state === 'blocked' && (
          <>
            <h1>Reconnect to continue</h1>
            <p>
              This device has been offline for more than 48 hours, so your BGD sign-in needs to
              be re-verified. Connect to the internet and retry — or sign in again directly.
            </p>
            <button className="auth-gate-signin" onClick={retry}>
              Retry
            </button>
            <button className="auth-gate-signin" onClick={() => void signInAgain()}>
              <MicrosoftMark />
              Sign in with Microsoft
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!msalConfigured) return;
    void ensureMsalInitialized().then(() => setReady(true));
  }, []);

  // Auth off (or dev bypass): the app behaves exactly as before.
  if (!msalConfigured || authDevBypass) return <>{children}</>;

  if (!ready) return <div className="auth-gate-screen" aria-busy="true" />;

  return (
    <MsalProvider instance={getMsalInstance()}>
      <AuthenticatedTemplate>
        <SessionGuard>{children}</SessionGuard>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInScreen />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
